(() => {
  if (customElements.get('product-section-nav')) return;

  class ProductSectionNav extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.bar = this.querySelector('nav');
      this.links = [...this.querySelectorAll('[data-target]')];
      this.owner = this.closest('.shopify-section');
      // Keep the sticky bar in the same document flow as the header. A fixed
      // viewport layer does not participate in native edge rubber-banding.
      this.main = this.closest('main');
      if (this.main) {
        this.main.before(this.bar);
        this.setAttribute('data-mounted', '');
        this.bar.setAttribute('data-product-section-nav', '');
      }
      this.refresh = this.refresh.bind(this);
      this.schedule = this.schedule.bind(this);
      this.bar.addEventListener('click', (event) => this.navigate(event), options);
      window.addEventListener('scroll', this.schedule, { ...options, passive: true });
      window.addEventListener('resize', this.schedule, options);
      window.addEventListener('hashchange', this.schedule, options);
      document.addEventListener('DOMContentLoaded', this.refresh, options);
      for (const name of ['load', 'unload', 'reorder']) {
        document.addEventListener(`shopify:section:${name}`, this.refresh, options);
      }
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(this.bar);
      // Also resolve app sections inserted after the initial page load.
      this.mutationObserver = new MutationObserver(this.refresh);
      this.mutationObserver.observe(document.getElementById('main') || document.body, { childList: true, subtree: true });
      this.refresh();
    }

    disconnectedCallback() {
      this.controller.abort();
      this.resizeObserver.disconnect();
      this.mutationObserver.disconnect();
      cancelAnimationFrame(this.frame);
      this.owner?.style.removeProperty('--product-section-nav-height');
      if (this.hasAttribute('data-mounted')) {
        this.removeAttribute('data-mounted');
        this.bar.removeAttribute('data-product-section-nav');
        this.append(this.bar);
      }
    }

    resolveTarget(value) {
      const exact = document.getElementById(value) || document.getElementById(`shopify-section-${value}`);
      if (exact) return exact;
      // A template prefix changes between products; the section key stays stable.
      return [...document.querySelectorAll('[id^="shopify-section-"]')]
        .find((section) => section.id.endsWith(`__${value}`));
    }

    refresh() {
      this.entries = this.links.map((link) => {
        const target = this.resolveTarget(link.dataset.target);
        link.hidden = !target;
        if (target) link.setAttribute('href', `#${target.id}`);
        return { link, target };
      }).filter((entry) => entry.target);
      this.hidden = !this.entries.length;
      this.bar.hidden = this.hidden;
      this.headers = [...document.querySelectorAll('.shopify-section--header, .shopify-section--announcement-bar')];
      this.resizeObserver.disconnect();
      this.resizeObserver.observe(this.bar);
      this.headers.forEach((header) => this.resizeObserver.observe(header));
      this.schedule();
    }

    headerBottom() {
      return this.headers.reduce((bottom, wrapper) => {
        const element = wrapper.querySelector('store-header') || wrapper;
        const rect = element.getBoundingClientRect();
        if (getComputedStyle(wrapper).visibility === 'hidden' || !element.getClientRects().length || rect.bottom <= 0) return bottom;
        return Math.max(bottom, rect.bottom);
      }, 0);
    }

    schedule() {
      // Follow the header's 200ms hide/show animation without a permanent loop.
      this.until = performance.now() + 300;
      if (!this.frame) this.frame = requestAnimationFrame(() => this.update());
    }

    update() {
      this.frame = null;
      if (!this.isConnected) return;
      if (this.hidden) {
        this.owner?.style.removeProperty('--product-section-nav-height');
        return;
      }
      const top = Math.max(0, this.headerBottom());
      const height = this.bar.getBoundingClientRect().height;
      // Exclude top-edge overscroll from the inset; native sticky positioning
      // already follows that movement with the rest of the document.
      const inset = Math.max(0, top + Math.min(0, window.scrollY));
      this.bar.style.setProperty('--product-section-nav-top', `${inset}px`);
      this.owner?.style.setProperty('--product-section-nav-height', `${height}px`);

      const entries = this.entries.filter(({ target }) => target.getClientRects().length)
        .sort((a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top);
      // No selection until a section reaches the navigation's reading line.
      let active = null;
      for (const entry of entries) {
        if (entry.target.getBoundingClientRect().top <= top + height + 24) active = entry;
      }
      if (active && window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2) active = entries.at(-1);
      for (const { link } of this.entries) {
        if (link === active?.link) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      }
      if (!active) this.activeLink = null;
      if (active && active.link !== this.activeLink) {
        this.activeLink = active.link;
        const scroller = this.activeLink.parentElement;
        const linkRect = this.activeLink.getBoundingClientRect();
        const scrollRect = scroller.getBoundingClientRect();
        if (linkRect.left < scrollRect.left || linkRect.right > scrollRect.right) {
          scroller.scrollTo({ left: scroller.scrollLeft + linkRect.left - scrollRect.left - (scrollRect.width - linkRect.width) / 2, behavior: 'auto' });
        }
      }
      if (performance.now() < this.until) this.frame = requestAnimationFrame(() => this.update());
    }

    navigate(event) {
      const link = event.target.closest('a[data-target]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const entry = this.entries.find((item) => item.link === link);
      if (!entry) return;
      event.preventDefault();
      // Reserve the expanded sticky header: scrolling upward can reveal it again.
      const stickyBottom = this.headers.reduce((bottom, wrapper) => {
        const style = getComputedStyle(wrapper);
        if (!['sticky', 'fixed'].includes(style.position)) return bottom;
        return Math.max(bottom, (parseFloat(style.top) || 0) + wrapper.getBoundingClientRect().height);
      }, 0);
      const offset = stickyBottom + this.bar.getBoundingClientRect().height + 12;
      window.scrollTo({
        top: Math.max(0, window.scrollY + entry.target.getBoundingClientRect().top - offset),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
      // Focus the destination without causing a second browser scroll.
      const target = entry.target;
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
        target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
      }
      target.focus({ preventScroll: true });
      this.schedule();
    }
  }
  customElements.define('product-section-nav', ProductSectionNav);
})();
